import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { format } from 'date-fns';
import {
  CheckCircle,
  XCircle,
  FileBox,
  Loader2,
  User,
  Calendar,
  Hash,
  ClipboardCheck,
  AlertTriangle,
  FolderOpen,
} from 'lucide-react';
import apiClient, { Evidence } from '../api/client';
import { EvidenceViewerModal } from '../components/evidence/EvidenceViewerModal';

function formatFileSize(bytes: number): string {
  if (!bytes || bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
}

export default function EvidenceIntake() {
  const queryClient = useQueryClient();
  const [rejectTarget, setRejectTarget] = useState<string | null>(null);
  const [rejectReason, setRejectReason] = useState('');
  const [viewEvidenceId, setViewEvidenceId] = useState<string | null>(null);
  const [activatingCase, setActivatingCase] = useState<string | null>(null);

  const { data: pendingEvidence = [], isLoading } = useQuery<Evidence[]>({
    queryKey: ['pending-evidence'],
    queryFn: () => apiClient.getPendingEvidence(),
    refetchInterval: 30_000,
  });

  // Fetch case statuses for any cases that appear in pending evidence
  const pendingCaseNumbers = [...new Set(
    pendingEvidence.map(e => e.caseNumber).filter(c => c && c !== 'PUBLIC-SUBMISSION')
  )];
  const { data: caseStatuses = {} } = useQuery<Record<string, string>>({
    queryKey: ['case-statuses', pendingCaseNumbers.join(',')],
    queryFn: async () => {
      const results: Record<string, string> = {};
      await Promise.all(pendingCaseNumbers.map(async cn => {
        try {
          const c = await apiClient.getCase(cn);
          results[cn] = c.status;
        } catch { /* ignore */ }
      }));
      return results;
    },
    enabled: pendingCaseNumbers.length > 0,
  });

  const acceptMutation = useMutation({
    mutationFn: (id: string) => apiClient.acceptEvidence(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['pending-evidence'] }),
  });

  const rejectMutation = useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) =>
      apiClient.rejectEvidence(id, reason),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pending-evidence'] });
      setRejectTarget(null);
      setRejectReason('');
    },
  });

  const handleAcceptAndActivate = async (evidenceId: string, caseNumber: string) => {
    setActivatingCase(caseNumber);
    try {
      await apiClient.acceptEvidence(evidenceId);
      await apiClient.updateCaseStatus(caseNumber, 'Active');
      queryClient.invalidateQueries({ queryKey: ['pending-evidence'] });
      queryClient.invalidateQueries({ queryKey: ['case-statuses'] });
      queryClient.invalidateQueries({ queryKey: ['cases'] });
    } finally {
      setActivatingCase(null);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-8 w-8 animate-spin text-primary-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start gap-3">
        <div className="p-2 bg-amber-100 rounded-lg flex-shrink-0 mt-0.5">
          <ClipboardCheck className="h-6 w-6 text-amber-700" />
        </div>
        <div className="flex-1">
          <h1 className="text-2xl font-bold text-gray-900">Evidence Intake</h1>
          <p className="text-sm text-gray-500 mt-0.5">
            Review evidence submitted by law enforcement. Accept to book it into the chain of
            custody; reject to return it with a reason.
          </p>
        </div>
        {pendingEvidence.length > 0 && (
          <span className="px-3 py-1 rounded-full bg-amber-100 text-amber-700 text-sm font-medium flex-shrink-0">
            {pendingEvidence.length} pending
          </span>
        )}
      </div>

      {/* Empty state */}
      {pendingEvidence.length === 0 && (
        <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
          <CheckCircle className="h-12 w-12 text-green-400 mx-auto mb-3" />
          <h3 className="text-lg font-semibold text-gray-900 mb-1">All caught up</h3>
          <p className="text-gray-500 text-sm">No evidence submissions are pending review.</p>
        </div>
      )}

      {/* Pending evidence list */}
      {pendingEvidence.length > 0 && (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="px-6 py-3 bg-amber-50 border-b border-amber-100 flex items-center gap-2 text-sm text-amber-800">
            <AlertTriangle className="h-4 w-4 flex-shrink-0" />
            These items have not yet entered the chain of custody. Accept to book them in; reject
            to remove with an audit record.
          </div>
          <ul className="divide-y divide-gray-100">
            {pendingEvidence.map((item) => (
              <li key={item.id} className="px-6 py-4 hover:bg-gray-50 transition-colors">
                <div className="flex items-start gap-4">
                  {/* Icon */}
                  <button
                    onClick={() => setViewEvidenceId(item.id)}
                    className="p-2 bg-gray-100 rounded-lg flex-shrink-0 mt-1 hover:bg-gray-200 transition-colors"
                    title="Preview evidence"
                  >
                    <FileBox className="h-5 w-5 text-gray-500" />
                  </button>

                  {/* Details */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <button
                        onClick={() => setViewEvidenceId(item.id)}
                        className="font-medium text-gray-900 hover:text-primary-600 truncate text-left"
                      >
                        {item.title || item.fileName}
                      </button>
                      <span className="px-2 py-0.5 text-xs font-medium bg-blue-100 text-blue-700 rounded flex-shrink-0">
                        {item.evidenceType}
                      </span>
                    </div>
                    <p className="text-sm text-gray-500 mt-0.5 truncate">
                      {item.fileName} &middot; {formatFileSize(item.fileSize)}
                    </p>
                    <div className="flex items-center flex-wrap gap-x-4 gap-y-1 mt-2 text-xs text-gray-500">
                      <span className="flex items-center gap-1">
                        <User className="h-3 w-3" />
                        {item.uploadedBy}
                      </span>
                      <span className="flex items-center gap-1">
                        <Calendar className="h-3 w-3" />
                        {format(new Date(item.uploadedAt), 'MMM d, yyyy h:mm a')}
                      </span>
                      <span className="flex items-center gap-1">
                        <Hash className="h-3 w-3" />
                        {item.caseNumber}
                      </span>
                      {caseStatuses[item.caseNumber] === 'Pending Review' && (
                        <span className="flex items-center gap-1 px-1.5 py-0.5 bg-orange-100 text-orange-700 rounded text-xs font-medium">
                          <FolderOpen className="h-3 w-3" />
                          Case Pending Review
                        </span>
                      )}
                    </div>
                    {item.description && (
                      <p className="text-xs text-gray-400 mt-1 italic truncate">
                        &ldquo;{item.description}&rdquo;
                      </p>
                    )}
                  </div>

                  {/* Actions */}
                  <div className="flex flex-col items-end gap-2 flex-shrink-0 mt-1">
                    {caseStatuses[item.caseNumber] === 'Pending Review' ? (
                      <button
                        onClick={() => handleAcceptAndActivate(item.id, item.caseNumber)}
                        disabled={activatingCase === item.caseNumber || acceptMutation.isPending}
                        title="Accept evidence and activate case"
                        className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-green-700 bg-green-50 border border-green-200 rounded-lg hover:bg-green-100 disabled:opacity-50 transition-colors"
                      >
                        {activatingCase === item.caseNumber ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <CheckCircle className="h-4 w-4" />
                        )}
                        Accept &amp; Activate
                      </button>
                    ) : (
                      <button
                        onClick={() => acceptMutation.mutate(item.id)}
                        disabled={acceptMutation.isPending}
                        title="Accept — books this evidence into the chain of custody"
                        className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-green-700 bg-green-50 border border-green-200 rounded-lg hover:bg-green-100 disabled:opacity-50 transition-colors"
                      >
                        {acceptMutation.isPending ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <CheckCircle className="h-4 w-4" />
                        )}
                        Accept
                      </button>
                    )}
                    <button
                      onClick={() => setRejectTarget(item.id)}
                      title="Reject — creates an audit record and removes from intake queue"
                      className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-red-700 bg-red-50 border border-red-200 rounded-lg hover:bg-red-100 transition-colors"
                    >
                      <XCircle className="h-4 w-4" />
                      Reject
                    </button>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Evidence Viewer Modal (for preview before deciding) */}
      <EvidenceViewerModal
        isOpen={viewEvidenceId !== null}
        onClose={() => setViewEvidenceId(null)}
        evidenceId={viewEvidenceId}
        onStatusChange={() => {
          queryClient.invalidateQueries({ queryKey: ['pending-evidence'] });
          setViewEvidenceId(null);
        }}
      />

      {/* Reject reason dialog */}
      {rejectTarget && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6 space-y-4">
            <h3 className="text-lg font-semibold text-gray-900">Reject Evidence</h3>
            <p className="text-sm text-gray-500">
              Provide a reason — this will be recorded in the custody audit trail and the
              submitting officer will be able to see it.
            </p>
            <textarea
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              placeholder="e.g. Incorrect case number, duplicate submission, file does not match description..."
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-red-400 focus:border-red-400 resize-none"
              autoFocus
            />
            <div className="flex gap-3">
              <button
                onClick={() => {
                  setRejectTarget(null);
                  setRejectReason('');
                }}
                className="flex-1 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={() =>
                  rejectMutation.mutate({ id: rejectTarget, reason: rejectReason })
                }
                disabled={!rejectReason.trim() || rejectMutation.isPending}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 disabled:opacity-50"
              >
                {rejectMutation.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
                Confirm Rejection
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
